import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useScopeTestingStatusRollup } from '../../../hooks/analytics/useScopeReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import { CHART_TOOLTIP_STYLE, chartColorFor } from '../theme.ts';

/** `scope_testing_status` — donut of current test runs by rollup status (draft/in_progress/completed/reviewed). */
export const ScopeTestingStatusChart: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useScopeTestingStatusRollup();

  const pieData = useMemo(
    () =>
      (data?.by_status ?? []).map((r) => ({
        name: r.status,
        value: r.count,
        color: chartColorFor(r.status),
      })),
    [data]
  );

  return (
    <WidgetCard
      title="Testing Status Rollup"
      description="Current test runs by workflow status"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load testing status rollup.'}
      isEmpty={!isLoading && !isError && pieData.length === 0}
      emptyMessage="No test runs recorded yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="relative h-40 w-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={3} stroke="none">
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number, name: string) => [value, name?.replace(/_/g, ' ')]} contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-900">{data?.total ?? 0}</span>
            <span className="text-[10px] text-slate-500">runs</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-600">
          {pieData.map((entry) => (
            <span key={entry.name} className="inline-flex items-center gap-1.5 capitalize">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name.replace(/_/g, ' ')} ({entry.value})
            </span>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
};

export default ScopeTestingStatusChart;
